/**
 * WorkspaceHub - Main dashboard landing page
 *
 * Redesigned as an analytics command center:
 * 1. Aggregate metric cards (views, events, sites, datapoints)
 * 2. Usage chart + compact account overview
 * 3. Enhanced sites directory with visual bars
 */

import { AMIR_CALL_URL } from "@/lib/constants/support";
import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  isTrialActivatingFresh,
  consumeOnboardingCheckout,
  markTrialActivating,
} from "@/lib/utils/trialActivation";
import { useAuth } from "@/lib/contexts/AuthContext";
import { useSubscription } from "@/lib/contexts/SubscriptionContext";
import { toast } from "sonner";
import { getPlanById } from "@/lib/constants/pricing";
import type { PricingPlan } from "@/lib/types/billing";

// API
import {
  getAllProperties,
  getTeamMembers,
  getPendingInvites,
  getAccountInfo,
} from "@/lib/api/api";
import { useAIUsage } from "@/lib/contexts/AIUsageContext";
import { getAvailableProperties } from "@/lib/api/workspace-team";
import type { TrackingStatus } from "@/lib/types/workspace-team";
import {
  getAllWorkspaces,
  type WorkspaceWithGrouping,
} from "@/lib/api/workspaces";
import {
  DEMO_PROPERTY_ID,
  DEMO_SITE_NAME,
} from "@/lib/constants";
import { getPaymentLink } from "@/lib/constants/pricing";
import { generateSlugId } from "@/lib/routing/slug-id";
import { track } from "@/lib/utils/track";
import { cn } from "@/lib/utils";

// UI
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

// Hub components — new utility-first dashboard layout
import SitesGrid, { pickFlaggedDrop } from "@/components/dashboard/hub/SitesGrid";
import { subMonths } from "date-fns";
import { PlanCard, McpCard } from "@/components/dashboard/hub/StatusCards";
import DashboardFooter from "@/components/dashboard/hub/DashboardFooter";
import CancelFlowDialog from "@/components/billing/CancelFlowDialog";
import SharedAccessNotification from "@/components/team/SharedAccessNotification";
import { OnboardingGate } from "@/components/onboarding/OnboardingGate";
import TrackingNotInstalledBanner from "@/components/dashboard/hub/TrackingNotInstalledBanner";
import { TrialBanner } from "@/components/shared/TrialBanner";
import AccountSetupPanel from "@/components/dashboard/hub/AccountSetupPanel";
import ListeningNotice from "@/components/dashboard/hub/ListeningNotice";
import HubLoading, { AddSiteButton, HubHeadline, HubTopBar } from "@/components/dashboard/hub/HubLoading";
import { siteTrackingState, waitingHeadline } from "@/components/dashboard/hub/siteTrackingState";

// Activation state hook drives which hero variant we render
import { useDashboardState } from "@/hooks/useDashboardState";
import { useApiKeyCount } from "@/hooks/useApiKeyCount";
import { useRealtimeStats } from "@/hooks/useRealtimeStats";
import { useExperiments } from "@/hooks/useExperiments";
import { useSavedFunnels } from "@/hooks/useAnalytics";
import { useAccountWoW } from "@/hooks/useAccountWoW";
import { useAccountTestCounts } from "@/hooks/useAccountTestCounts";
import { useAccountRevenue } from "@/hooks/useAccountRevenue";
import { useBiggestFunnelDrop } from "@/hooks/useBiggestFunnelDrop";
import { useOnboardingProgress } from "@/hooks/useOnboardingProgress";

// Icons

// ── Types ────────────────────────────────────────────────────────────────────

interface PropertyWorkspaceAttachment {
  workspaceId: string;
  name: string;
  isAutoGrouped?: boolean;
}
import { HugeiconsIcon } from "@hugeicons/react"
import {
  AlertCircleIcon,
  ArrowRight01Icon,
  BookOpen01Icon,
  EarthIcon,
  Folder01Icon,
  Mail01Icon,
  PlusSignIcon,
  Settings01Icon,
} from "@hugeicons/core-free-icons"

interface Property {
  propertyId: string;
  name: string;
  domain: string;
  archived?: boolean;
  isStaging?: boolean;
  isDemo?: boolean;
  dataSources?: Array<{ config?: { domain?: string } }>;
  stats?: { views?: number; events?: number };
  attachedWorkspaces?: PropertyWorkspaceAttachment[];
  /** From GET /properties. There is no server-side write for this today, so
   *  traffic arriving is the signal that actually clears the install state. */
  trackingStatus?: TrackingStatus;
  /** Traffic in the last 7 days / 7-14 days. Durable across a quiet month,
   *  unlike the month-to-date counters in `stats`. */
  hasRecentViews?: boolean;
  hasPreviousViews?: boolean;
}

interface TeamMember {
  user_id: string;
  email: string;
  is_owner: boolean;
  propertyCount?: number;
}

interface AccountInfo {
  plan?: string;
  planName?: string;
  teamSeatLimit?: number;
  features?: {
    splitTests?: number | boolean;
    funnels?: number | boolean;
    heatmaps?: number | boolean;
    teamAccess?: boolean;
  };
  usage?: {
    datapoints?: number;
    datapointLimit?: number;
    sites?: number;
    siteLimit?: number;
  };
  subscription?: {
    currentPlan?: string;
    total?: number;
    subLimit?: number;
    websiteLimit?: number;
    activeSplitTestLimit?: number;
    savedFunnelLimit?: number;
    activeExperiments?: number;
    teamSeatLimit?: number;
    planSource?: string;
    localTrialActive?: boolean;
  };
}

// Curated resources
const RESOURCES = [
  {
    title: "How to Get started",
    description: "Fast and straightforward guide to create your account and connect your site.",
    url: "https://docs.humblytics.com/how-to-get-started",
  },
  {
    title: "Split Testing Under the Hood",
    description: "Understand how Humblytics split testing works with performance-first optimization.",
    url: "https://docs.humblytics.com/split-testing-overview/how-humblytics-split-testing-works-under-the-hood",
  },
  {
    title: "Understanding Your Data",
    description: "Learn how to interpret and act on your analytics data.",
    url: "https://docs.humblytics.com/understanding-your-data",
  },
  {
    title: "Plan Limits & Upgrades",
    description: "Understanding plan limits, add-ons, and how to upgrade or change your plan.",
    url: "https://docs.humblytics.com/frequently-asked-questions",
  },
];

const ENTERPRISE_CAL_URL = AMIR_CALL_URL;
const UNLIMITED_LIMIT = 999999999;

/** Safely parse numeric values to prevent scientific notation overflow in display */
const safeNum = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n < 1e9 ? n : 0;
};

/** Compact number formatter used by the billing pill and inline stats. */
const formatCompactNumber = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toLocaleString();
};

/** Inline header stat — uppercase mini-label + value, separated by a center dot. */
// ── Component ────────────────────────────────────────────────────────────────

export default function WorkspaceHub() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user, logout } = useAuth();
  const {
    usedTrial: realUsedTrial,
    hasReachedWebsiteLimit,
    websitesUsed,
    websiteLimit,
    isFreePlan: realSubIsFreePlan,
    hasActiveTrial: realSubHasActiveTrial,
    hasActiveSubscription: realSubHasActiveSubscription,
    trialDaysLeft: realSubTrialDaysLeft,
    trialEndsAt: subTrialEndsAt,
    eventsUsed: subEventsUsed,
    eventLimit: subEventLimit,
    currentPlan: realSubPlanTier,
    planName: realSubPlanName,
    stripeTrialActivated: realSubStripeTrialing,
    isReadOnly,
    readOnlyReason,
    subscriptionStatus,
    openPricingModal,
    openUpdatePortal,
    isLoading: subLoading,
  } = useSubscription();

  // Data state
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [activeTab, setActiveTab] = useState("websites");
  const [properties, setProperties] = useState<Property[]>([]);
  const usedTrial = realUsedTrial;
  const subIsFreePlan = realSubIsFreePlan;
  const subHasActiveTrial = realSubHasActiveTrial;
  const subHasActiveSubscription = realSubHasActiveSubscription;
  // True when the Stripe redirect has returned but the webhook hasn't landed.
  const isPlanActivating =
    (!!searchParams.get("stripe_session_id") || isTrialActivatingFresh()) &&
    subIsFreePlan &&
    !subHasActiveTrial &&
    !subHasActiveSubscription;

  // If the user just returned from the onboarding Stripe step, send them into
  // the wizard instead of keeping them on /dashboard.
  const onboardingResumeCheckedRef = useRef(false);
  useEffect(() => {
    if (onboardingResumeCheckedRef.current) return;
    if (!searchParams.get("stripe_session_id")) return;
    onboardingResumeCheckedRef.current = true;
    // Always stamp the activating flag on Stripe return so TrialActivationWatcher
    // starts polling — regardless of whether the onboarding checkout flag is set.
    markTrialActivating();
    if (consumeOnboardingCheckout()) {
      track("onboarding_resumed_post_checkout", token, {});
      navigate("/onboarding/profile", { replace: true });
    }
  }, []);
  const subTrialDaysLeft = realSubTrialDaysLeft;
  const subPlanTier = realSubPlanTier;
  const subPlanName = realSubPlanName;
  const subStripeTrialing = realSubStripeTrialing;
  const [workspaces, setWorkspaces] = useState<WorkspaceWithGrouping[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [currentTeamUser, setCurrentTeamUser] = useState<TeamMember | null>(null);
  const [pendingInvites, setPendingInvites] = useState<any[]>([]);
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);

  // AI usage from shared context
  const {
    usage: aiUsage,
    usagePercent: aiUsagePercent,
    isApproachingLimit: aiApproachingLimit,
    hasAIAccess,
  } = useAIUsage();

  // UI state
  const [cancelFlowOpen, setCancelFlowOpen] = useState(false);
  const [isBillingLoading, setIsBillingLoading] = useState(false);

  useEffect(() => {
    document.title = "Dashboard - Humblytics";
  }, []);

  // The global AppPaywallController mounted in App.tsx handles persistent
  // blocking for no-trial/no-subscription accounts on /dashboard. Upgrade
  // buttons and plan card CTAs call openPricingModal() from SubscriptionContext
  // so the controller shows the dialog dismissibly for non-blocked users.

  useEffect(() => {
    if (!token) {
      navigate("/sign-in");
      return;
    }
    loadAllData();
  }, [token]);

  // ── Data Loading ─────────────────────────────────────────────────────────

  const loadAllData = async () => {
    if (!token) return;
    setIsLoading(true);
    setLoadError(false);
    try {
      const [
        propertiesRes,
        workspacesRes,
        availablePropsRes,
        teamRes,
        invitesRes,
        accountRes,
      ] = await Promise.allSettled([
        getAllProperties(token),
        getAllWorkspaces(token),
        getAvailableProperties(token),
        getTeamMembers(token),
        getPendingInvites(token),
        getAccountInfo(token),
      ]);

      if (propertiesRes.status === "rejected") {
        setLoadError(true);
        setIsLoading(false);
        return;
      }

      // Workspaces
      const loadedWorkspaces: WorkspaceWithGrouping[] =
        workspacesRes.status === "fulfilled" ? workspacesRes.value || [] : [];
      setWorkspaces(loadedWorkspaces);

      // Build property → workspace map
      const propertyAttachments = new Map<string, PropertyWorkspaceAttachment[]>();
      // Same pass also captures tracking_status so the hub can flag sites whose
      // script was never installed. No extra request.
      const trackingStatusById = new Map<string, TrackingStatus>();

      if (
        availablePropsRes.status === "fulfilled" &&
        availablePropsRes.value?.properties
      ) {
        for (const prop of availablePropsRes.value.properties) {
          if (prop.tracking_status) {
            trackingStatusById.set(prop.property_id, prop.tracking_status);
          }
          if (prop.current_workspace_id) {
            const ws = loadedWorkspaces.find(
              (w) => w.workspaceId === prop.current_workspace_id
            );
            if (ws) {
              propertyAttachments.set(prop.property_id, [
                { workspaceId: ws.workspaceId, name: ws.name, isAutoGrouped: false },
              ]);
            }
          } else {
            propertyAttachments.set(prop.property_id, []);
          }
        }
      }

      // Add auto-grouped
      for (const ws of loadedWorkspaces) {
        if (ws.isAutoGrouped && ws.properties) {
          for (const prop of ws.properties) {
            const existing = propertyAttachments.get(prop.propertyId) || [];
            if (!existing.some((a) => a.workspaceId === ws.workspaceId)) {
              existing.push({
                workspaceId: ws.workspaceId,
                name: ws.name,
                isAutoGrouped: true,
              });
              propertyAttachments.set(prop.propertyId, existing);
            }
          }
        }
      }

      // Properties
      if (propertiesRes.status === "fulfilled" && propertiesRes.value?.properties) {
        const seen = new Set<string>();
        const userProperties = propertiesRes.value.properties
          .filter((property: any) => {
            if (seen.has(property.propertyId)) return false;
            seen.add(property.propertyId);
            return true;
          })
          .map((property: any) => ({
          ...property,
          isStaging: property.is_staging ?? property.isStaging ?? false,
          archived: property.archived ?? false,
          stats: {
            views: safeNum(property.subscription?.currentViews),
            events: safeNum(property.subscription?.currentEvents),
          },
          hasRecentViews: property.subscription?.hasRecentViews ?? property.hasRecentViews,
          hasPreviousViews: property.subscription?.hasPreviousViews ?? property.hasPreviousViews,
          attachedWorkspaces: propertyAttachments.get(property.propertyId) || [],
          trackingStatus:
            trackingStatusById.get(property.propertyId) ??
            property.tracking_status ??
            property.trackingStatus,
        }));

        const demoProperty = {
          propertyId: DEMO_PROPERTY_ID,
          name: DEMO_SITE_NAME,
          domain: "demo.humblytics.com",
          isDemo: true,
          archived: false,
          isStaging: false,
        };
        setProperties([demoProperty, ...userProperties]);

        // Subscription data
        if (propertiesRes.value.subscription) {
          const sub = propertiesRes.value.subscription;
          setAccountInfo({
            ...(accountRes.status === "fulfilled" ? accountRes.value : {}),
            subscription: sub,
            usage: {
              datapoints: sub.total || 0,
              datapointLimit: sub.subLimit || 0,
              siteLimit: sub.websiteLimit || 0,
            },
            planName: sub.currentPlan || "Free",
            plan: sub.currentPlan || "Free",
            features: {
              splitTests: sub.activeSplitTestLimit || 0,
              funnels: sub.savedFunnelLimit || 0,
              heatmaps: sub.savedFunnelLimit || 0,
              teamAccess: sub.teamSeatLimit > 1,
            },
            teamSeatLimit: sub.teamSeatLimit || 1,
          });
        } else if (accountRes.status === "fulfilled" && accountRes.value) {
          setAccountInfo(accountRes.value);
        }
      } else {
        const demoProperty = {
          propertyId: DEMO_PROPERTY_ID,
          name: DEMO_SITE_NAME,
          domain: "demo.humblytics.com",
          isDemo: true,
          archived: false,
          isStaging: false,
        };
        setProperties([demoProperty]);
        if (accountRes.status === "fulfilled" && accountRes.value) {
          setAccountInfo(accountRes.value);
        }
      }

      // Team
      if (teamRes.status === "fulfilled") {
        if (teamRes.value?.users) setTeamMembers(teamRes.value.users);
        if (teamRes.value?.currentUser) setCurrentTeamUser(teamRes.value.currentUser);
      }
      if (invitesRes.status === "fulfilled" && invitesRes.value?.invites) {
        setPendingInvites(invitesRes.value.invites);
      }
    } catch (error) {
      console.error("Error loading workspace hub data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  };

  // ── Computed Values ──────────────────────────────────────────────────────

  const activeSiteCount = useMemo(
    () => properties.filter((p) => !p.archived && !p.isStaging && !p.isDemo).length,
    [properties]
  );

  const totalViews = useMemo(
    () => properties.reduce((sum, p) => sum + (p.stats?.views || 0), 0),
    [properties]
  );

  const totalEvents = useMemo(
    () => properties.reduce((sum, p) => sum + (p.stats?.events || 0), 0),
    [properties]
  );

  const usedSeats =
    (currentTeamUser ? 1 : 0) + teamMembers.length + pendingInvites.length;
  const seatLimit = accountInfo?.teamSeatLimit || 1;
  const isUnlimitedSeats = seatLimit >= UNLIMITED_LIMIT;
  const hasTeamAccess = seatLimit > 1 || accountInfo?.features?.teamAccess === true;

  const datapointsUsed =
    accountInfo?.subscription?.total || accountInfo?.usage?.datapoints || 0;
  const datapointsLimit =
    accountInfo?.subscription?.subLimit || accountInfo?.usage?.datapointLimit || 1000000;
  const datapointsPercent =
    datapointsLimit > 0 ? Math.min((datapointsUsed / datapointsLimit) * 100, 100) : 0;

  const sitesUsed = activeSiteCount;
  const sitesLimit =
    accountInfo?.subscription?.websiteLimit || accountInfo?.usage?.siteLimit || 0;
  const isUnlimitedSites = sitesLimit >= UNLIMITED_LIMIT;

  const planName =
    accountInfo?.subscription?.currentPlan ||
    accountInfo?.planName ||
    accountInfo?.plan ||
    "Free";

  const planNameToId = (name: string): string => {
    const lower = name?.toLowerCase() || "";
    if (lower.includes("plus") && lower.includes("legacy")) return "plus_legacy";
    return lower.replace(/\s+/g, "") || "free";
  };
  const currentPlanId = planNameToId(planName);
  const hasActiveSubscription =
    !(planName === "Free" || !planName) &&
    !(
      accountInfo?.subscription?.planSource === "local_trial" &&
      accountInfo?.subscription?.localTrialActive
    );

  // `/billing/status` doesn't return the billing interval yet, so the card shows
  // the monthly list price (the checkout default) rather than guessing annual.
  const isAnnualBilling = false;

  const currentPlan = getPlanById(
    currentPlanId as "plus" | "business" | "scale"
  ) ?? (currentPlanId === "plus_legacy" ? getPlanById("plus") : null);

  const isTopPlan = currentPlanId === "enterprise";

  const exploreBasePath = useMemo(() => {
    const first = properties.find((p) => !p.isDemo && !p.archived && !p.isStaging);
    if (!first) return null;
    const slugId = generateSlugId(first.name || first.domain, first.propertyId);
    return `/p/${slugId}`;
  }, [properties]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleOpenBillingPortal = async () => {
    if (!token) return;

    const isFreePlan = planName === "Free" || !planName;
    const isLocalTrial =
      accountInfo?.subscription?.planSource === "local_trial" &&
      accountInfo?.subscription?.localTrialActive;

    if (!accountInfo?.subscription || isFreePlan || isLocalTrial) {
      openPricingModal();
      return;
    }

    setIsBillingLoading(true);
    try {
      const opened = await openUpdatePortal();
      if (!opened) {
        toast.error("There was an error opening your billing portal.", {
          description: "Please try again later.",
        });
      }
    } catch {
      toast.error("There was an error opening your billing portal.", {
        description: "Please try again later.",
      });
    } finally {
      setIsBillingLoading(false);
    }
  };

  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  // ── Dashboard state (drives hero variant) ──────────────────────────────────
  // Must run BEFORE any early return — hooks must be unconditional.
  const firstProperty =
    properties.find((p) => !p.isDemo && !p.archived) ??
    properties.find((p) => !p.isDemo) ??
    properties[0]
  const firstPropertyId = firstProperty?.propertyId ?? null
  const nonDemoSiteCount = useMemo(
    () => properties.filter((p) => !p.isDemo && !p.archived).length,
    [properties]
  )

  // Account-wide fan-out hooks — must be declared BEFORE useDashboardState
  // because the variant cascade reads their outputs. All React-Query-cached,
  // all parallel. Together they unlock every remaining hero variant + the
  // per-row test-count chip + the AI insight strip's WoW data.
  // Data hooks always fetch against real properties (avoids 500s when dev
  // override uses mock propertyIds). Their outputs may then be masked by the
  // dev override below.
  const accountWoW = useAccountWoW({ properties })
  const accountTestCounts = useAccountTestCounts({ properties })
  const accountRevenue = useAccountRevenue({ properties })
  const biggestFunnelDrop = useBiggestFunnelDrop({ properties })

  const dashboardState = useDashboardState({
    propertyId: firstPropertyId,
    totalEvents: totalViews + totalEvents,
    // Mature-account signals — any of these short-circuits the activation wizard
    // hero so paid / multi-site users see the steady "what changed" variant
    // instead of "Step 2 of 5 — verify tracking."
    hasPaidPlan: subHasActiveSubscription,
    hasActiveTrial: subHasActiveTrial,
    siteCount: nonDemoSiteCount,
    // Real data feeding the variant cascade — drives copy + numbers in the hero.
    viewsToday: totalViews,
    sites: properties
      .filter((p) => !p.isDemo && !p.archived)
      .map((p) => ({ propertyId: p.propertyId, name: p.name || p.domain, domain: p.domain })),
    // WoW deltas per site — enables TOP_MOVER variant when any site spikes >20%.
    siteDeltas: accountWoW.deltasByPropertyId,
    // Biggest funnel drop across the account — enables FUNNEL_DROP variant.
    funnelDrop: biggestFunnelDrop.topDrop
      ? {
          from: biggestFunnelDrop.topDrop.from,
          to: biggestFunnelDrop.topDrop.to,
          deltaPct: biggestFunnelDrop.topDrop.dropPct,
        }
      : undefined,
    // Revenue WoW signal — enables REVENUE_ANOMALY variant (Stripe-gated).
    revenue: accountRevenue.isConfigured && accountRevenue.deltaPct != null
      ? {
          thisWeek: accountRevenue.revenueThisWeek,
          lastWeek: accountRevenue.revenueLastWeek,
          deltaPct: accountRevenue.deltaPct,
          topContributorName: accountRevenue.topContributor?.name,
          topContributorSharePct: accountRevenue.topContributor?.sharePct,
        }
      : undefined,
  })

  // Live signals — drive the Agent/API card variant and the Realtime ribbon.
  // Both fail silently to zero/null if endpoints reject or the user lacks access,
  // so the dashboard always renders something sensible.
  const { count: apiKeyCount } = useApiKeyCount(firstPropertyId)
  const realtime = useRealtimeStats(firstPropertyId)

  // Real experiments + saved funnels feed the Active Tests card. These hooks
  // dedup against the dashboardState hook's identical queries via React Query
  // cache, so this isn't a second fetch.
  const { data: experiments = [], isLoading: experimentsLoading } = useExperiments({
    propertyId: firstPropertyId ?? undefined,
    token: token ?? undefined,
  })
  const { data: savedFunnels = [], isLoading: funnelsLoading } = useSavedFunnels({
    propertyId: firstPropertyId ?? undefined,
  })
  const onboardingProgress = useOnboardingProgress()

  // Instrument the gate. Only "no_site" actually blocks now — unverified
  // tracking is a soft state, so reporting it here would overcount the event
  // against a wall that no longer exists.
  const gateReasonRef = useRef<string | null>(null);
  useEffect(() => {
    if (onboardingProgress.isLoading) return;
    const reason = onboardingProgress.realSiteCount === 0 ? "no_site" : null;
    if (reason && gateReasonRef.current !== reason) {
      gateReasonRef.current = reason;
      track("onboarding_gate_blocked", token, {
        reason,
        real_site_count: onboardingProgress.realSiteCount,
      });
    }
    if (!reason) gateReasonRef.current = null;
  }, [
    onboardingProgress.isLoading,
    onboardingProgress.realSiteCount,
    token,
  ]);

  // basePath for in-app routing — prefer property-scoped route when we have
  // one. Declared up here (above the early-loading-return) because the
  // useMemo hooks below depend on it; React's rules-of-hooks requires the
  // hook count be identical between loading + loaded renders.
  const basePath = firstPropertyId
    ? `/p/${generateSlugId(firstProperty?.name || firstProperty?.domain || '', firstPropertyId)}`
    : ''

  // Workspace-scoped base for routes that only live under /workspace/...
  // (e.g. /api). Falls back to the property-scoped basePath when the first
  // property has no attached workspace.
  const workspaceBasePath = useMemo(() => {
    if (!firstPropertyId) return basePath
    const prop = firstProperty
    if (prop?.attachedWorkspaces?.length) {
      const ws = prop.attachedWorkspaces[0]
      return `/workspace/${generateSlugId(ws.name, ws.workspaceId)}`
    }
    return basePath
  }, [firstPropertyId, firstProperty, basePath])

  // Per-site path resolver shared between AIInsightStrip and SitesDirectory.
  // Must be defined BEFORE the early return so the hook order stays stable
  // between the loading render and the loaded render.
  const resolveSitePath = useMemo(
    () => (propertyId: string, subPath: string): string => {
      const prop = properties.find((p) => p.propertyId === propertyId)
      if (!prop) return basePath + subPath
      if (prop.attachedWorkspaces?.length) {
        const ws = prop.attachedWorkspaces[0]
        const workspaceSlugId = generateSlugId(ws.name, ws.workspaceId)
        return `/workspace/${workspaceSlugId}${subPath}?site=${propertyId}`
      }
      const slugId = generateSlugId(prop.name || prop.domain, propertyId)
      return `/p/${slugId}${subPath}`
    },
    [properties, basePath]
  )

  // Sites we have never received data from and whose tag was never verified.
  //
  // Careful: subscription.currentViews/currentEvents are month-to-date
  // (`WHERE time >= date_trunc('month', NOW())` in the backend), NOT lifetime.
  // `siteTrackingState` therefore also requires both 14-day windows
  // (hasRecentViews = last 7d, hasPreviousViews = 7-14d) to be empty. A site
  // can still be dormant rather than broken, which is why the banner reports
  // "no data received" instead of asserting the script is missing.
  const sitesNeedingInstall = useMemo(
    () =>
      properties
        .filter((p) => !p.isDemo && !p.archived && siteTrackingState(p) === "install")
        .map((p) => ({ propertyId: p.propertyId, domain: p.domain })),
    [properties]
  )
  // Verified tag, no visit yet: the grey "Listening" state between install and
  // live. Same rule as the SitesGrid pill.
  const sitesWaiting = useMemo(
    () =>
      properties
        .filter((p) => !p.isDemo && !p.archived && siteTrackingState(p) === "waiting")
        .map((p) => ({ propertyId: p.propertyId, domain: p.domain })),
    [properties]
  )


  // Sites with traffic — feed to AIInsightStrip for cross-site analysis.
  const sitesForInsight = useMemo(
    () =>
      properties
        .filter((p) => !p.isDemo && !p.archived)
        .map((p) => ({
          propertyId: p.propertyId,
          name: p.name || p.domain,
          domain: p.domain,
          views: p.stats?.views ?? 0,
          events: p.stats?.events ?? 0,
        })),
    [properties]
  )

  // Activation users still get the original ordering. Mature users get the
  // ops-triage ordering with AI insight surface + demoted Agent/API.
  const isMatureUserComputed = dashboardState.step === 'steady'
  // Gate only the truly empty account. Unverified tracking is a soft state
  // (TrackingNotInstalledBanner + an Install pill on the row), never a wall —
  // a trial user who just paid should not be locked out of the product.
  // `isFetching` is deliberately absent: including it re-rendered the whole
  // gate on every remount of the hub, which read as "the dashboard threw me
  // back into onboarding". Matches DashboardLayout's policy so the two agree.
  //
  // Cross-checked against the hub's own property list. useOnboardingProgress
  // runs with retry:false, so one failed fetch (cold backend, blip, expired
  // token) leaves realSiteCount at the EMPTY_PROGRESS default of 0. Gating on
  // that alone would drop an "add your first site" wall over an established
  // account's dashboard on a transient error. Two independent sources must
  // agree the account is empty before we take the screen over.
  const hubRealSiteCount = properties.filter((p) => !p.isDemo && !p.archived).length
  const showOnboardingGate =
    !onboardingProgress.isLoading &&
    !isLoading &&
    onboardingProgress.realSiteCount === 0 &&
    hubRealSiteCount === 0

  // Nothing on the account has ever reported. The hub has no content to render
  // in this state, so it collapses to AccountSetupPanel: one job, one CTA.
  const isAccountDark =
    !showOnboardingGate &&
    hubRealSiteCount > 0 &&
    sitesNeedingInstall.length === hubRealSiteCount

  // Track time-to-usable-state once — when the user lands here right after
  // onboarding (pending record exists in localStorage with a siteAddedAt stamp).
  const sitesGridRef = useRef<HTMLDivElement | null>(null);
  const dashboardUsableTrackedRef = useRef(false);
  useEffect(() => {
    if (dashboardUsableTrackedRef.current || showOnboardingGate) return;
    try {
      const raw = localStorage.getItem("humblytics:generation:pending:v1");
      if (!raw) return;
      const record = JSON.parse(raw);
      if (!record?.siteAddedAt) return;
      const msSinceSiteAdd = Date.now() - new Date(record.siteAddedAt).getTime();
      if (msSinceSiteAdd > 30 * 60 * 1000) return; // ignore if > 30 min old
      dashboardUsableTrackedRef.current = true;
      track("dashboard_usable", token, { msSinceSiteAdd, propertyId: record.propertyId });
    } catch { /* ignore */ }
  }, [showOnboardingGate, token]);

  // ── Loading ──────────────────────────────────────────────────────────────

  if (loadError) {
    return (
      <div className="w-full max-w-[1120px] mx-auto px-4 sm:px-6 py-4 flex flex-col items-center justify-center gap-4 min-h-[400px]">
        <div className="flex flex-col items-center gap-3 text-center max-w-sm">
          <HugeiconsIcon icon={AlertCircleIcon} size={32} className="text-muted-foreground" />
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-foreground">Unable to load dashboard</p>
            <p className="text-xs text-muted-foreground">We couldn't reach the server. Check your connection and try again.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => loadAllData()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading && !showOnboardingGate) {
    return <HubLoading basePath={workspaceBasePath} onAddSite={() => navigate('/add-website')} />;
  }

  // ── Render ───────────────────────────────────────────────────────────────

  // basePath is computed up top (above the early-loading-return) because hooks
  // below depend on it.

  // Plan state — prefer SubscriptionContext (canonical) over legacy accountInfo.
  // accountInfo still feeds the older PricingModal flow; we keep it but the
  // Plan card now reads from the typed SubscriptionContext fields.
  const dashboardPlanName = subPlanName || planName || currentPlan?.name || (accountInfo as any)?.planName || 'Free'
  const dashboardSitesLimit = websiteLimit || sitesLimit || 0
  // Source of truth is the count actually rendered in the sites grid above —
  // any other number diverges from what the user sees and erodes trust.
  // (`websitesUsed` from SubscriptionContext returns stale/incorrect counts
  // in some accounts; using it as primary made the Plan card claim "111 sites"
  // alongside a grid of 7. Don't.)
  const dashboardSitesUsed = nonDemoSiteCount

  // Greeting — first name only. Don't fall back to the email handle (e.g.
  // "am+2982" reads as uncanny); plain "there" is friendlier when name is unset.
  const greetingName = user?.name?.split(' ')[0] || 'there'

  // resolveSitePath, sitesForInsight, and isMatureUser are now declared
  // ABOVE the early loading return (see top of component). They're used in
  // the JSX below.
  const isMatureUser = showOnboardingGate ? false : isMatureUserComputed

  // Answer-first headline (Paper "App v3 · Home"): the account's visitor
  // delta this month, then the one site that needs action. Same numbers the
  // Sites table shows: unique visitors MTD vs the same days last month.
  const liveProperties = properties.filter((p) => !p.isDemo && !p.archived)
  let visitorsThisMonth = 0
  let visitorsPriorSpan = 0
  for (const p of liveProperties) {
    const summary = accountWoW.byPropertyId[p.propertyId]
    visitorsThisMonth += summary?.thisWeek?.sessions ?? 0
    visitorsPriorSpan += summary?.lastWeek?.sessions ?? 0
  }
  const accountVisitorDelta =
    visitorsPriorSpan > 0
      ? Math.round(((visitorsThisMonth - visitorsPriorSpan) / visitorsPriorSpan) * 100)
      : null
  const flaggedDrop = pickFlaggedDrop(liveProperties, accountWoW.byPropertyId)
  const now = new Date()
  const monthName = now.toLocaleDateString('en-US', { month: 'long' })
  const priorMonthName = subMonths(now, 1).toLocaleDateString('en-US', { month: 'long' })
  const hour = now.getHours()
  const timeGreeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
  const hasHeadlineData = !accountWoW.isLoading && accountVisitorDelta !== null
  // Every real site verified and silent: say so instead of welcoming or
  // reporting a delta we cannot have (Paper "Home · Waiting for first visitor").
  const waiting = waitingHeadline(
    liveProperties.map((p) => ({ domain: p.domain || p.name, state: siteTrackingState(p) })),
    `${timeGreeting}, ${greetingName}.`,
  )
  const headline = (() => {
    if (waiting) return waiting.headline
    if (!isMatureUser) return `Welcome, ${greetingName}. Let's get you set up.`
    if (!hasHeadlineData) return `${timeGreeting}, ${greetingName}.`
    const d = accountVisitorDelta!
    const total =
      d <= -1
        ? `Visitors are down ${Math.abs(d)}% this month.`
        : d >= 1
          ? `Visitors are up ${d}% this month.`
          : 'Visitors are flat this month.'
    return flaggedDrop
      ? `${total} ${flaggedDrop.domain} is down ${Math.abs(flaggedDrop.delta)}%.`
      : total
  })()
  const siteCountLabel = `${nonDemoSiteCount} site${nonDemoSiteCount === 1 ? '' : 's'}`
  const subline = waiting
    ? waiting.subline
    : !isMatureUser
    ? null
    : hasHeadlineData
      ? `${timeGreeting}, ${greetingName}. ${monthName} so far across ${siteCountLabel}, compared with the same days in ${priorMonthName}.`
      : `${monthName} so far across ${siteCountLabel}.`

  return (
    <div className="flex min-h-full flex-col">
      {/* Top bar — 52px, page title + the view's one orange CTA */}
      <HubTopBar>
        {hasReachedWebsiteLimit && (isReadOnly || websiteLimit > 0) && (
          <span className="hidden text-xs leading-4 text-muted-foreground sm:inline">
            {isReadOnly ? (readOnlyReason === 'trial_ended' ? 'Trial ended. ' : 'Subscription ended. ') : `${websitesUsed} of ${websiteLimit} sites. `}
            <button
              onClick={() => openPricingModal()}
              className="font-medium text-foreground hover:underline"
            >
              Upgrade
            </button>
          </span>
        )}
        {!showOnboardingGate && (
          <AddSiteButton
            onClick={() => navigate('/add-website')}
            // Demoted while the account is dark: installing the tracker is the
            // only primary action until something reports.
            quiet={isAccountDark}
            disabled={hasReachedWebsiteLimit}
          />
        )}
      </HubTopBar>

      {/* Monetization strips (read-only, payment failed, trial, quota), same as every dashboard page */}
      <TrialBanner />

      <div className="flex flex-col gap-6 px-4 pb-9 pt-7 sm:px-9">
      {/* Pending workspace invites */}
      <SharedAccessNotification />

      {/* Answer-first headline */}
      {!showOnboardingGate && <HubHeadline headline={headline} subline={subline} />}

      {showOnboardingGate && (
        <OnboardingGate onSiteAdded={loadAllData} />
      )}

      {/* Zero-state: nothing has ever reported, so the hub collapses to one job.
          The setup panel absorbs both banners, which otherwise contradict each
          other here (install the script vs. launch a test). */}
      {isAccountDark && (
        <AccountSetupPanel
          sites={sitesNeedingInstall}
        />
      )}

      {/* Verified but silent: grey, self-clearing, never a wall. */}
      {!showOnboardingGate && !isAccountDark && sitesWaiting.length > 0 && (
        <ListeningNotice
          sites={sitesWaiting}
          onOpenSite={(site) =>
            track("listening_notice_open_site_clicked", token, { property_id: site.propertyId })
          }
        />
      )}

      {/* Partially dark account: the hub is useful, but flag the silent sites. */}
      {!showOnboardingGate && !isAccountDark && sitesNeedingInstall.length > 0 && (
        <TrackingNotInstalledBanner
          sites={sitesNeedingInstall}
          onReviewSites={() =>
            sitesGridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
          }
        />
      )}

      {/* Plan + MCP — compact strip above sites so the table stays above the fold */}
      <div className={cn("well grid grid-cols-1 gap-2 md:grid-cols-2", isAccountDark && "hidden")}>
        <PlanCard
          planName={dashboardPlanName}
          planTier={subPlanTier}
          isFreePlan={subIsFreePlan}
          hasActiveTrial={subHasActiveTrial}
          isStripeTrialing={subStripeTrialing}
          hasActiveSubscription={subHasActiveSubscription}
          usedTrial={usedTrial}
          trialDaysLeft={subTrialDaysLeft}
          trialEndsAt={subTrialEndsAt}
          monthlyPrice={isAnnualBilling ? currentPlan?.annualPrice ?? currentPlan?.monthlyPrice : currentPlan?.monthlyPrice}
          isAnnualBilling={isAnnualBilling}
          subscriptionStatus={subscriptionStatus}
          isReadOnly={isReadOnly}
          datapointsUsed={subEventsUsed || datapointsUsed}
          datapointsLimit={subEventLimit || datapointsLimit}
          sitesUsed={dashboardSitesUsed}
          sitesLimit={dashboardSitesLimit}
          seatsUsed={usedSeats}
          seatsLimit={seatLimit}
          hasTeamAccess={hasTeamAccess}
          onUpgrade={() => openPricingModal()}
          onStartTrial={() => openPricingModal()}
          onManageBilling={handleOpenBillingPortal}
          onCancelPlan={
            subHasActiveSubscription && !isReadOnly
              ? () => setCancelFlowOpen(true)
              : undefined
          }
          isLoading={subLoading}
          isActivating={isPlanActivating}
        />
        <McpCard basePath={workspaceBasePath} />
      </div>

      {/* Sites table — always shown when user has at least one site */}
      {!showOnboardingGate && !isAccountDark && properties.filter((p) => !p.isDemo).length >= 1 && (
        <div ref={sitesGridRef} className="well">
        <SitesGrid
          properties={properties}
          byPropertyId={accountWoW.byPropertyId}
          flaggedPropertyId={flaggedDrop?.propertyId ?? null}
          testCountsByProperty={accountTestCounts.byPropertyId}
          resolveSitePath={resolveSitePath}
          onOpenSiteSettings={(property, opts) => {
            const slugId = generateSlugId(
              property.name || property.domain,
              property.propertyId,
            );
            const tab = opts?.tab ? `?tab=${opts.tab}` : '';
            navigate(`/p/${slugId}/settings${tab}`);
          }}
          onSiteArchived={() => loadAllData()}
          isLoading={accountWoW.isLoading || experimentsLoading}
        />
        </div>
      )}

      {/* Footer */}
      <DashboardFooter />
      </div>

      {/* ── Modals ──────────────────────────────────────────────────── */}
      <CancelFlowDialog
        open={cancelFlowOpen}
        onOpenChange={setCancelFlowOpen}
        mode="cancel"
        surface="hub_plan_card"
      />

    </div>
  );
}
